'use client';

import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, SelectChangeEvent, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LabelSelectInput from '../../formControls/LabelSelectInput';
import FilterPillValueSegment from './FilterPillValueSegment';
import { pillSelectSx, segmentDivider } from './filterPillStyles';
import debounce from 'lodash/debounce';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import {
  FieldOption,
  FilterInput,
  LogicalOperator,
  OperatorOption,
} from '../../constants';
import {
  CatalogueDetailAnimalsOptions,
  EbvConfigItem,
  buildEbvFieldOptions,
  getAllFieldOptionsForSearchType,
  InputTypeByField,
  MEMBER_LOOKUP_FIELD_PREFIX,
  SaleCatalogueSelectOptions,
  type DynamicFieldOption,
} from '../constants';
import { getOperatorsByField } from '../helpers';
import { lookupsWithI6Mapping } from '../expandI6LookupFilters';
import {
  parseRangeCompositeValue,
  RangeCompositeValue,
  stringifyRangeCompositeValue,
} from '../rangeValue';
import {
  isEbvAccuracyField,
  isEbvField,
  isEbvPercentileField,
} from '../ebvFilterFields';
import { legacyFilterToChipValue } from '../animalIdentChipFilter';
import {
  editIdentifierOperatorFilter,
  getIdentifierOperatorFilter,
  IDENTIFIER_PATTERN_OPERATOR,
  isIdentifierOperatorField,
} from '../identifierOperatorFilter';

interface Props extends FilterInput {
  index: number;
  onUpdateFilter?: (index: number, filter: Partial<FilterInput>) => void;
  onRemoveFilter?: (index: number) => void;
  onUpdateOperator?: (op: LogicalOperator) => void;
  groupOperator?: LogicalOperator;
}

export default function FilterPill({
  field,
  index,
  operator,
  value,
  onUpdateFilter,
  onRemoveFilter,
}: Props) {
  const [filterValue, setFilterValue] = useState<string>(value ?? '');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets local input mirror when the controlled `value` prop changes externally; filterValue is also written by user-edit handlers, so it cannot be a pure derived value
    setFilterValue(value ?? '');
  }, [value]);

  const searchType = useSearchType();
  const society = usePublicSearchContext((context) => context.society);
  const store = useFilterStore(searchType);

  const updateFilter = onUpdateFilter ?? store.updateFilter;
  const removeFilter = onRemoveFilter ?? store.removeFilter;

  const debounceUpdateFilter = useMemo(
    () => debounce(updateFilter, 300),
    [updateFilter],
  );

  const fieldIsEbv = isEbvField(field);
  const fieldIsEbvAccuracy = isEbvAccuracyField(field);
  const fieldIsEbvPercentile = isEbvPercentileField(field);
  const fieldIsAnyEbvType =
    fieldIsEbv || fieldIsEbvAccuracy || fieldIsEbvPercentile;
  const isSexField = field === FieldOption.sex.value;
  const isDetailAnimalsSearch = DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType);
  const isAnimalQuickSearchField =
    (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) &&
    (field === FieldOption.searchTerm.value ||
      field === FieldOption.dam.value ||
      field === FieldOption.sire.value);
  // Animal-tab breeder/owner chips, plus Members-tab composite MEMBER field
  // (same MemberIdentMultiSelect UX as Basic Search).
  const isMemberQuickSearchField =
    ((searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) &&
      (field === FieldOption.breeder.value ||
        field === FieldOption.currentOwner.value)) ||
    (searchType === SEARCH_TYPE.MEMBERS && field === FieldOption.member.value);
  const isQuickSearchField =
    isAnimalQuickSearchField || isMemberQuickSearchField;
  const isDateField = field === FieldOption.dateOfBirth.value;
  const isFreeTextIdentifierField = isIdentifierOperatorField(field);
  const identifierFilter = getIdentifierOperatorFilter(operator, value);
  const identifierDisplayOperator = identifierFilter.isLegacyPattern
    ? IDENTIFIER_PATTERN_OPERATOR
    : identifierFilter.operator;
  const isCalvingYearField = field === FieldOption.calvingYear.value;
  // Active Sale Listings is a binary condition (saleCount > 0 / = 0) rendered
  // as a toggle within the group rather than a field + operator + value row.
  const isSaleCountField = field === FieldOption.saleCount.value;
  // Member-search lookup pill (e.g. ms_grp_select_1 → "Member Zone").
  // Society-defined preset whose `i6LookupString` already encodes the
  // operator + value; the pill collapses to a description dropdown.
  const isLookupField =
    typeof field === 'string' && field.startsWith(MEMBER_LOOKUP_FIELD_PREFIX);

  const useEbvSpecializedInputs = fieldIsAnyEbvType && !operator;
  const inputType = fieldIsAnyEbvType ? 'number' : InputTypeByField[field];
  const isBetweenOperator =
    operator === OperatorOption.between.value &&
    (inputType === 'number' || inputType === 'date');

  const isTwoPart =
    isSexField ||
    isDateField ||
    isCalvingYearField ||
    useEbvSpecializedInputs ||
    isQuickSearchField ||
    isLookupField ||
    isSaleCountField;

  // Free-text and lookup value inputs should stretch to fill the row instead of
  // being squeezed to their content width; the compact multi-part numeric/date
  // pickers (EBV, date range, between, sex, calving year) stay content-sized.
  const isDefaultTextValue =
    !useEbvSpecializedInputs &&
    !isSexField &&
    !isDateField &&
    !isCalvingYearField &&
    !isQuickSearchField &&
    !isFreeTextIdentifierField &&
    !isLookupField &&
    !isSaleCountField &&
    !isBetweenOperator;
  const fillWidth =
    isQuickSearchField ||
    isLookupField ||
    isFreeTextIdentifierField ||
    isDefaultTextValue;
  const stackTextSegments =
    !isTwoPart && (isFreeTextIdentifierField || isDefaultTextValue);

  // ── EBV state ──
  const [ebvInputs, setEbvInputs] = useState<RangeCompositeValue>(() => {
    if (!useEbvSpecializedInputs) return {};
    const parsed = parseRangeCompositeValue(value);
    return { min: parsed?.min || '', max: parsed?.max || '' };
  });

  useEffect(() => {
    if (!useEbvSpecializedInputs) return;
    const parsed = parseRangeCompositeValue(value);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-syncs the parsed min/max EBV inputs when field/value props change; ebvInputs is also mutated by handleEbvInputChange and handleFieldChange, so it is not purely derived
    setEbvInputs({ min: parsed?.min || '', max: parsed?.max || '' });
  }, [field, useEbvSpecializedInputs, value]);

  const debouncedEbvUpdate = useMemo(
    () =>
      debounce((newInputs: RangeCompositeValue) => {
        updateFilter(index, { value: stringifyRangeCompositeValue(newInputs) });
      }, 300),
    [index, updateFilter],
  );

  const handleEbvInputChange = useCallback(
    (inputKey: keyof RangeCompositeValue, inputValue: string) => {
      // Skip incomplete numeric drafts (e.g. typing "-" before digits); allow negatives.
      const trimmed = inputValue.trim();
      if (trimmed === '-' || trimmed === '-.' || trimmed === '.') return;
      setEbvInputs((prev) => {
        const newInputs = { ...prev, [inputKey]: inputValue };
        debouncedEbvUpdate(newInputs);
        return newInputs;
      });
    },
    [debouncedEbvUpdate],
  );

  // ── Between range state ──
  const [rangeInputs, setRangeInputs] = useState<RangeCompositeValue>(() => {
    if (!isBetweenOperator) return {};
    const parsed = parseRangeCompositeValue(value);
    return { min: parsed?.min || '', max: parsed?.max || '' };
  });

  useEffect(() => {
    if (!isBetweenOperator) return;
    const parsed = parseRangeCompositeValue(value);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- re-syncs the parsed between-range min/max when the value prop changes externally; rangeInputs is also written by handleRangeInputChange and handleFieldChange, so it is not purely derived
    setRangeInputs({ min: parsed?.min || '', max: parsed?.max || '' });
  }, [isBetweenOperator, value]);

  const handleRangeInputChange = useCallback(
    (inputKey: keyof RangeCompositeValue, inputValue: string) => {
      setRangeInputs((prev) => {
        const newInputs = { ...prev, [inputKey]: inputValue };
        const hasValue = !!(newInputs.min?.trim() || newInputs.max?.trim());
        debounceUpdateFilter(index, {
          value: hasValue ? stringifyRangeCompositeValue(newInputs) : '',
        });
        return newInputs;
      });
    },
    [debounceUpdateFilter, index],
  );

  // Animal / dam / sire pills use the chip-style picker; the operator is
  // encoded per-chip via wildcards (`X%`, `%X%`, `%X`). Legacy rows that
  // still carry an explicit operator are projected into chip form for
  // display and re-stored as `{ operator: '', value }` on the next edit.
  const animalIdentChipValue = useMemo(
    () => legacyFilterToChipValue(operator ?? '', filterValue ?? ''),
    [operator, filterValue],
  );

  const handleAnimalIdentChipChange = useCallback(
    (next: string) => {
      setFilterValue(next);
      updateFilter(index, { operator: '', value: next });
    },
    [index, updateFilter],
  );

  const handleIdentifierValueChange = (nextValue: string) => {
    const next = editIdentifierOperatorFilter(operator, filterValue, {
      value: nextValue,
    });
    setFilterValue(next.value);
    updateFilter(index, next);
  };

  // ── Sex state ──
  const handleSexValueChange = useCallback(
    (v: string) => {
      setFilterValue(v);
      if (!v) {
        updateFilter(index, { operator: '', value: '' });
      } else {
        updateFilter(index, {
          operator: OperatorOption.equals.value,
          value: v,
        });
      }
    },
    [index, updateFilter],
  );

  // ── Calving year state ──
  const handleCalvingYearValueChange = useCallback(
    (v: string) => {
      setFilterValue(v);
      if (!v) {
        updateFilter(index, { operator: '', value: '' });
      } else {
        updateFilter(index, {
          operator: OperatorOption.equals.value,
          value: v,
        });
      }
    },
    [index, updateFilter],
  );

  // ── Date range state ──
  const [dateRangeInputs, setDateRangeInputs] = useState<RangeCompositeValue>(
    () => {
      if (!isDateField) return {};
      if (operator === OperatorOption.between.value) {
        const parsed = parseRangeCompositeValue(value);
        return { min: parsed?.min || '', max: parsed?.max || '' };
      }
      if (
        operator === OperatorOption.greaterThan.value ||
        operator === OperatorOption.greaterThanOrEqual.value
      ) {
        return { min: value, max: '' };
      }
      if (
        operator === OperatorOption.lessThan.value ||
        operator === OperatorOption.lessThanOrEqual.value
      ) {
        return { min: '', max: value };
      }
      return {};
    },
  );

  useEffect(() => {
    if (!isDateField) return;
    if (operator === OperatorOption.between.value) {
      const parsed = parseRangeCompositeValue(value);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- re-syncs the date-range inputs from operator/value props when they change externally; dateRangeInputs is also written by handleDateRangeChange and handleFieldChange, so it is not purely derived
      setDateRangeInputs({ min: parsed?.min || '', max: parsed?.max || '' });
    } else if (
      operator === OperatorOption.greaterThan.value ||
      operator === OperatorOption.greaterThanOrEqual.value
    ) {
      setDateRangeInputs({ min: value, max: '' });
    } else if (
      operator === OperatorOption.lessThan.value ||
      operator === OperatorOption.lessThanOrEqual.value
    ) {
      setDateRangeInputs({ min: '', max: value });
    } else if (!operator && !value) {
      setDateRangeInputs({});
    }
  }, [isDateField, operator, value]);

  const handleDateRangeChange = useCallback(
    (inputKey: keyof RangeCompositeValue, inputValue: string) => {
      setDateRangeInputs((prev) => {
        const newInputs = { ...prev, [inputKey]: inputValue };
        const hasMin = !!newInputs.min?.trim();
        const hasMax = !!newInputs.max?.trim();
        if (!hasMin && !hasMax) {
          updateFilter(index, { operator: '', value: '' });
        } else {
          updateFilter(index, {
            operator: OperatorOption.between.value,
            value: stringifyRangeCompositeValue(newInputs),
          });
        }
        return newInputs;
      });
    },
    [index, updateFilter],
  );

  // ── EBV config ──
  const ebvConfig = useMemo<EbvConfigItem[]>(() => {
    if (!society.settings || !Array.isArray(society.settings)) return [];
    const ebvColumnsSetting = society.settings.find(
      (setting) =>
        setting &&
        setting.societySettingType === SOCIETY_SETTING_TYPE.EBV_FIELDS,
    );
    if (!ebvColumnsSetting || !ebvColumnsSetting.value) return [];
    try {
      const parsed = JSON.parse(ebvColumnsSetting.value) as EbvConfigItem[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [society.settings]);

  const isEbvEnabled = useIsEbvEnabled();
  const { showPercentileSearch } = useEbvDisplayOptions();
  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );

  const ebvFieldOptionParams = useMemo(
    () => ({ includePercentileFields: showPercentileSearch }),
    [showPercentileSearch],
  );

  const fieldOptions = useMemo(() => {
    if (searchType === SEARCH_TYPE.ANIMAL) {
      return getAllFieldOptionsForSearchType(
        searchType,
        [],
        effectiveEbvConfig,
        undefined,
        society.settings,
        undefined,
        showPercentileSearch,
      );
    }
    if (searchType === SEARCH_TYPE.MEMBERS) {
      // Sourced from MEMBER_SEARCH_FIELDS (SocietySettingType=19) so the
      // dropdown matches the society's configured search fields exactly,
      // in the order they configured them, with their custom labels —
      // same source the basic-search dynamic section uses.
      return getAllFieldOptionsForSearchType(
        SEARCH_TYPE.MEMBERS,
        [],
        [],
        undefined,
        society.settings,
      );
    }
    if (DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)) {
      const ebvOptions = buildEbvFieldOptions(
        effectiveEbvConfig,
        ebvFieldOptionParams,
      );
      return [...CatalogueDetailAnimalsOptions, ...ebvOptions];
    }
    if (searchType === SEARCH_TYPE.SALE_CATALOGUES)
      return SaleCatalogueSelectOptions;
    return [];
  }, [
    searchType,
    effectiveEbvConfig,
    society.settings,
    ebvFieldOptionParams,
    showPercentileSearch,
  ]);

  const operatorOptions = useMemo(() => {
    const options = getOperatorsByField(field);
    if (!isFreeTextIdentifierField) return options;
    if (identifierFilter.isLegacyPattern) {
      return [
        { label: 'Existing pattern', value: IDENTIFIER_PATTERN_OPERATOR },
        ...options,
      ];
    }
    if (
      identifierFilter.operator &&
      !options.some((item) => item.value === identifierFilter.operator)
    ) {
      return [
        ...options,
        { label: identifierFilter.operator, value: identifierFilter.operator },
      ];
    }
    return options;
  }, [
    field,
    isFreeTextIdentifierField,
    identifierFilter.operator,
    identifierFilter.isLegacyPattern,
  ]);

  const selectedFieldOption = useMemo<DynamicFieldOption | undefined>(() => {
    return (fieldOptions as DynamicFieldOption[]).find(
      (o) => o.value === field,
    );
  }, [field, fieldOptions]);

  const fieldLabel = useMemo(() => {
    return (
      selectedFieldOption?.label ||
      Object.values(FieldOption).find((option) => option.value === field)
        ?.label ||
      field.replace(/^__lookup__/, '').replace(/_/g, ' ')
    );
  }, [selectedFieldOption, field]);

  /** Lookup choices for the currently-selected lookup-marker field. */
  const lookupChoices = useMemo(
    () =>
      isLookupField ? lookupsWithI6Mapping(selectedFieldOption?.lookups) : [],
    [isLookupField, selectedFieldOption],
  );

  function handleFieldChange(e: SelectChangeEvent<unknown>) {
    const newField = e.target.value as string;
    const newFilter = { field: newField } as FilterInput;

    const newFieldIsEbv = isEbvField(newField);
    const newFieldIsEbvAccuracy = isEbvAccuracyField(newField);
    const newFieldIsEbvPercentile = isEbvPercentileField(newField);
    const newFieldIsAnyEbv =
      newFieldIsEbv || newFieldIsEbvAccuracy || newFieldIsEbvPercentile;
    const newFieldIsLookup =
      typeof newField === 'string' &&
      newField.startsWith(MEMBER_LOOKUP_FIELD_PREFIX);

    if (newField === FieldOption.saleCount.value) {
      // Binary toggle: default to "has active sale listings" (saleCount > 0).
      newFilter.operator = OperatorOption.greaterThan.value;
      newFilter.value = '0';
    } else if (newFieldIsAnyEbv) {
      newFilter.operator = '';
      newFilter.value = stringifyRangeCompositeValue({ min: '', max: '' });
      setEbvInputs({ min: '', max: '' });
    } else if (newFieldIsLookup) {
      // Pre-set LOOKUP so build pipeline can resolve via the i6LookupString.
      // Value is the picked lookup `code`; cleared until the user picks one.
      newFilter.operator = 'LOOKUP';
      newFilter.value = '';
    } else {
      newFilter.operator = '';
      newFilter.value = '';
    }

    setFilterValue('');
    setRangeInputs({ min: '', max: '' });
    setDateRangeInputs({});
    updateFilter(index, newFilter);
  }

  // ── Segment renderers ──

  const renderFieldSegment = () => (
    <Box
      sx={{
        ...segmentDivider,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'action.hover',
        borderRadius: '20px 0 0 20px',
        minWidth: 0,
        flexShrink: 0,
        height: '100%',
        ...(stackTextSegments && {
          gridColumn: '1 / 3',
          gridRow: '1',
          borderRadius: { xs: '20px 0 0 0', md: '20px 0 0 20px' },
          borderRightWidth: { xs: 0, md: 1 },
          height: { xs: '38px', md: '100%' },
        }),
      }}
    >
      <LabelSelectInput
        options={fieldOptions}
        selectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected) {
              return (
                <Typography
                  variant="labelmedium"
                  sx={{
                    color: 'inherit',
                    opacity: 0.5,
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Select field...
                </Typography>
              );
            }
            return (
              <Typography
                variant="labelmedium"
                sx={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
              >
                {fieldLabel || 'Field'}
              </Typography>
            );
          },
          onChange: handleFieldChange,
          sx: { ...pillSelectSx },
        }}
        selectedValue={field || ''}
        sx={{
          minWidth: 0,
          ...(stackTextSegments && {
            width: { xs: '100%', md: 'auto' },
          }),
        }}
      />
    </Box>
  );

  const renderOperatorSegment = () => (
    <Box
      sx={{
        ...segmentDivider,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        flexShrink: 0,
        ...(stackTextSegments && {
          gridColumn: '1',
          gridRow: '2',
          borderTopWidth: { xs: 1, md: 0 },
          borderTopStyle: 'solid',
          borderTopColor: 'grey.300',
          minWidth: 0,
          height: { xs: '38px', md: '100%' },
        }),
      }}
    >
      <LabelSelectInput
        options={operatorOptions}
        selectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected) {
              return (
                <Typography
                  variant="labelmedium"
                  sx={{ color: 'inherit', opacity: 0.5, fontSize: '13px' }}
                >
                  op
                </Typography>
              );
            }
            const option = operatorOptions.find((o) => o.value === selected);
            return (
              <Typography
                variant="labelmedium"
                sx={{ fontSize: '13px', whiteSpace: 'nowrap' }}
              >
                {option?.label || String(selected)}
              </Typography>
            );
          },
          onChange: (e) => {
            const nextOperator = e.target.value as string;
            if (isFreeTextIdentifierField) {
              if (nextOperator === IDENTIFIER_PATTERN_OPERATOR) return;
              const next = editIdentifierOperatorFilter(operator, filterValue, {
                operator: nextOperator,
              });
              setFilterValue(next.value);
              updateFilter(index, next);
              return;
            }
            if (nextOperator === OperatorOption.between.value) {
              setFilterValue('');
              setRangeInputs({ min: '', max: '' });
              updateFilter(index, { operator: nextOperator, value: '' });
              return;
            }
            if (operator === OperatorOption.between.value) {
              setFilterValue('');
              updateFilter(index, { operator: nextOperator, value: '' });
              return;
            }
            updateFilter(index, { operator: nextOperator });
          },
          sx: { ...pillSelectSx },
        }}
        selectedValue={
          isFreeTextIdentifierField ? identifierDisplayOperator : operator || ''
        }
        sx={{
          minWidth: 0,
          ...(stackTextSegments && {
            width: { xs: '100%', md: 'auto' },
          }),
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={(theme) => ({
        display: fillWidth ? 'flex' : 'inline-flex',
        alignItems: 'center',
        borderRadius: '20px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'grey.300',
        backgroundColor: 'background.paper',
        height:
          isFreeTextIdentifierField && identifierFilter.isLegacyPattern
            ? 'auto'
            : '38px',
        ...(stackTextSegments && {
          display: { xs: 'grid', md: 'flex' },
          gridTemplateColumns: '120px minmax(0, 1fr) auto',
          height: {
            xs: 'auto',
            md: identifierFilter.isLegacyPattern ? 'auto' : '38px',
          },
        }),
        minHeight: '38px',
        overflow: 'visible',
        // Text / lookup pills grow to fill the group row; compact pills hug content.
        ...(fillWidth ? { flex: '1 1 100%' } : { flexShrink: 0 }),
        maxWidth: '100%',
        transition: 'box-shadow 0.15s',
        '&:hover': {
          boxShadow: `0 0 0 1px ${(theme.vars || theme).palette.grey[400]}`,
        },
      })}
    >
      {renderFieldSegment()}

      {!isTwoPart && renderOperatorSegment()}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          px: 0.5,
          ...(fillWidth && { flex: 1, minWidth: 0 }),
          ...(stackTextSegments && {
            gridColumn: '2 / -1',
            gridRow: '2',
            height: { xs: 'auto', md: '100%' },
            minHeight: '38px',
            borderTopWidth: { xs: 1, md: 0 },
            borderTopStyle: 'solid',
            borderTopColor: 'grey.300',
          }),
        }}
      >
        <FilterPillValueSegment
          index={index}
          field={field}
          operator={
            isFreeTextIdentifierField ? identifierFilter.operator : operator
          }
          searchType={searchType}
          filterValue={
            isFreeTextIdentifierField ? identifierFilter.value : filterValue
          }
          inputType={inputType}
          isSaleCountField={isSaleCountField}
          isLookupField={isLookupField}
          useEbvSpecializedInputs={useEbvSpecializedInputs}
          isQuickSearchField={isQuickSearchField}
          isAnimalQuickSearchField={isAnimalQuickSearchField}
          isSexField={isSexField}
          isDateField={isDateField}
          isFreeTextIdentifierField={isFreeTextIdentifierField}
          identifierIsLegacyPattern={identifierFilter.isLegacyPattern}
          isCalvingYearField={isCalvingYearField}
          isBetweenOperator={isBetweenOperator}
          ebvInputs={ebvInputs}
          dateRangeInputs={dateRangeInputs}
          rangeInputs={rangeInputs}
          animalIdentChipValue={animalIdentChipValue}
          lookupChoices={lookupChoices}
          updateFilter={updateFilter}
          debounceUpdateFilter={debounceUpdateFilter}
          setFilterValue={setFilterValue}
          handleEbvInputChange={handleEbvInputChange}
          handleAnimalIdentChipChange={handleAnimalIdentChipChange}
          handleIdentifierValueChange={handleIdentifierValueChange}
          handleSexValueChange={handleSexValueChange}
          handleDateRangeChange={handleDateRangeChange}
          handleCalvingYearValueChange={handleCalvingYearValueChange}
          handleRangeInputChange={handleRangeInputChange}
        />
      </Box>

      <IconButton
        size="small"
        onClick={() => removeFilter(index)}
        sx={{
          width: '26px',
          height: '26px',
          mr: '4px',
          flexShrink: 0,
          color: 'text.secondary',
          ...(stackTextSegments && { gridColumn: '3', gridRow: '1' }),
          '&:hover': { color: 'error.main' },
        }}
      >
        <CloseIcon sx={{ fontSize: '16px' }} />
      </IconButton>
    </Box>
  );
}
